import { useTranslation } from 'react-i18next'
import { hasAtLeast, partition } from 'remeda'
import { SimpleRelatedSolesContainer } from './SimpleRelatedSoles.styles'

/**
 * Creates a product link HTML string with accessibility attributes.
 */
const createSoleLink = (
  sole: { handle: string; title: string },
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  // Use the translation for aria-label, similar to how soleAlternative_one works
  /**
   * Remove any HTML tags for aria-label.
   */
  const ariaLabel = t('soleAlternative_one', { product: sole.title })
    .replaceAll('&lt;0&gt;&lt;/0&gt;', sole.title.toLowerCase())
    .replaceAll(/<[^>]*>/g, '')

  return `<a href="/products/${sole.handle}" aria-label="${ariaLabel}" rel="nofollow">${sole.title.toLowerCase()}</a>`
}

/**
 * Processes translation with multiple replacements.
 */
const processTranslation = (
  translation: string,
  replacements: Record<string, string>,
): string => {
  let processedText = translation

  for (const [placeholder, replacement] of Object.entries(replacements)) {
    processedText = processedText.replaceAll(placeholder, replacement)
  }

  return processedText
}

/**
 * Renders the final result with dangerouslySetInnerHTML and accessibility attributes.
 */
const renderResult = (processedText: string): JSX.Element => {
  return (
    <SimpleRelatedSolesContainer
      dangerouslySetInnerHTML={{ __html: processedText }}
    />
  )
}

interface SimpleRelatedSolesProps {
  soles: { handle: string; title: string; icon?: string }[]
  variantHandle: string
}

export const SimpleRelatedSoles = ({
  soles,
  variantHandle,
}: SimpleRelatedSolesProps): JSX.Element | null => {
  const { t } = useTranslation('product')

  if (soles.length === 1) {
    const sole = soles[0]

    const rawTranslation = t('soleAlternative_one')

    const processedText = processTranslation(rawTranslation, {
      '&lt;0&gt;&lt;/0&gt;': sole.title.toLowerCase(),
    })

    return renderResult(processedText)
  }

  if (soles.length === 2) {
    const rawTranslation = t('soleAlternative_two')
    const [currentSoles, otherSoles] = partition(
      soles,
      (sole) => sole.handle === variantHandle,
    )

    const processedText = processTranslation(rawTranslation, {
      '&lt;0&gt;&lt;/0&gt;': currentSoles[0].title.toLowerCase(),
      '&lt;1&gt;&lt;/1&gt;': createSoleLink(otherSoles[0], t),
    })

    return renderResult(processedText)
  }

  if (hasAtLeast(soles, 3)) {
    const manyTranslation = t('soleAlternative_many')
    const joinerTranslation = t('soleAlternative_joiner')
    const [currentSoles, otherSoles] = partition(
      soles,
      (sole) => sole.handle === variantHandle,
    )

    const otherSoleLinks = otherSoles
      .map((sole) => {
        const link = createSoleLink(sole, t)

        return processTranslation(joinerTranslation, {
          '&lt;0&gt;{{sole}}&lt;/0&gt;': link,
          '{{sole}}': sole.title.toLowerCase(),
        })
      })
      .join(', ')

    const processedText = processTranslation(manyTranslation, {
      '&lt;0&gt;{{sole1}}&lt;/0&gt;': currentSoles[0].title.toLowerCase(),
      '{{sole1}}': currentSoles[0].title.toLowerCase(),
      '{{otherSoles}}': otherSoleLinks,
    })

    return renderResult(processedText)
  }

  return null
}
